import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { taskTemplateService } from '../../../services/taskTemplateService';
import { useProtocols } from '../../../hooks/useProtocols';
import { useNotifications } from '../../NotificationSystem';
import './SetupWizardSteps.css';

const Step10Tasks = ({ stepData, onComplete, onValidation, userData, structure }) => {
  const { notify } = useNotifications();
  const { t } = useTranslation('setup');
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const organizationId = structure?.organizations?.[0]?.id || null;
  const { protocols } = useProtocols({});

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const allTemplates = await taskTemplateService.getAllTemplates();
        setTemplates(allTemplates || []);
        if ((allTemplates || []).length >= 2) {
          onComplete({ templates: allTemplates.length, requirementsMet: true });
        }
      } catch (error) {
        console.error('Error loading templates:', error);
      }
    };
    loadTemplates();
  }, []);

  const handleCreateBasicTemplates = async () => {
    if (!organizationId) {
      notify.error(t('step10.notifications.missingOrganization'), t('step10.notifications.missingOrganizationTitle'));
      return;
    }

    if (!protocols || protocols.length === 0) {
      notify.error(t('step10.notifications.missingProtocols'), t('step10.notifications.missingProtocolsTitle'));
      return;
    }

    setLoading(true);
    try {
      const userId = userData?.uid || 'system';

      const basicTemplates = [
        {
          name: t('step10.defaultTemplates.example1.name'),
          description: t('step10.defaultTemplates.example1.description'),
          taskType: 'protocol_execution',
          protocolId: protocols[0]?.id,
          priority: 'medium',
          estimatedDuration: protocols[0]?.totalEstimatedDuration || 30,
          steps: protocols[0]?.steps || [],
          requiredResources: [],
          isActive: true
        },
        {
          name: t('step10.defaultTemplates.example2.name'),
          description: t('step10.defaultTemplates.example2.description'),
          taskType: 'protocol_execution',
          protocolId: protocols[1]?.id || protocols[0]?.id,
          priority: 'medium',
          estimatedDuration: protocols[1]?.totalEstimatedDuration || 30,
          steps: protocols[1]?.steps || protocols[0]?.steps || [],
          requiredResources: [],
          isActive: true
        }
      ];

      for (const template of basicTemplates) {
        await taskTemplateService.createTemplate(template, userId);
      }

      const allTemplates = await taskTemplateService.getAllTemplates();
      setTemplates(allTemplates || []);

      notify.success(t('step10.notifications.createdSuccess'), t('step10.notifications.createdSuccessTitle'));
      onComplete({ templates: (allTemplates || []).length, requirementsMet: true });
    } catch (error) {
      console.error('Error creating templates:', error);
      notify.error(t('step10.notifications.createFailed'), t('step10.notifications.createFailedTitle'));
    } finally {
      setLoading(false);
    }
  };

  const requirements = {
    templates: templates.length >= 2
  };

  return (
    <div className="step-content">
      <div className="step-section">
        <h3>{t('step10.title')}</h3>
        <p>{t('step10.description')}</p>
      </div>

      {!organizationId && (
        <div className="error-message" style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#fff3cd', color: '#856404', borderRadius: '6px' }}>
          {t('step10.completeStep1Warning')}
        </div>
      )}

      {(!protocols || protocols.length === 0) && (
        <div className="error-message" style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#fff3cd', color: '#856404', borderRadius: '6px' }}>
          {t('step10.completeStep7Warning')}
        </div>
      )}

      <div className="quick-setup-section">
        <div className="quick-setup-option" style={{ cursor: loading || !organizationId || !protocols || protocols.length === 0 ? 'not-allowed' : 'pointer', opacity: loading || !organizationId || !protocols || protocols.length === 0 ? 0.6 : 1 }}>
          <div style={{ textAlign: 'center' }}>
            <h3>{t('step10.quickSetupTitle')}</h3>
            <p>{t('step10.quickSetupDescription')}</p>
            <ul style={{ textAlign: 'left', marginTop: '12px', paddingLeft: '20px' }}>
              <li>{t('step10.quickSetupList1')}</li>
              <li>{t('step10.quickSetupList2')}</li>
            </ul>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleCreateBasicTemplates}
              disabled={loading || !organizationId || !protocols || protocols.length === 0}
              style={{ marginTop: '16px' }}
            >
              {loading ? t('step10.creating') : t('step10.createBasicTemplates')}
            </button>
          </div>
        </div>
      </div>

      <div className="requirements-section" style={{ marginTop: '32px' }}>
        <h3>{t('step10.templatesTitle')}</h3>
        <div className="requirements-list">
          <div className={`requirement-item ${requirements.templates ? 'met' : 'pending'}`}>
            {requirements.templates ? <CheckCircle size={20} color="#4caf50" /> : <AlertCircle size={20} color="#ff9800" />}
            <span>{t('step10.templatesCount', { count: templates.length })}</span>
          </div>
        </div>

        {requirements.templates && (
          <div className="success-message" style={{ marginTop: '20px' }}>
            {t('step10.minimumMet')}
          </div>
        )}
      </div>

      <div className="manual-setup-section" style={{ marginTop: '32px' }}>
        <p style={{ color: '#666', fontStyle: 'italic' }}>
          {t('step10.manualSetupNote')}
        </p>
      </div>
    </div>
  );
};

export default Step10Tasks;
